import * as React from "react";
import { Field, FieldSet } from "../modelDialogs/BaseDialog";
import TemplateGroup from "../models/TemplateGroup";
import DeviceProductDetails from "./DeviceProductDetails";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import { useEffect, useState } from "react";
import { channelMethode } from "../models/SettingsEnums";
import { useRedraw } from "../models/useRerender";
import { showToast } from "./Toasts";
import type Device from "../models/Device";
import { itx, noItx } from "../translationItx/itx";
import { ValueLevels } from "../models/KnxInstanceData";
import { KnxProductConfigParam } from "../KnxProductConfig";
import KnxProductData from "../KnxProductdata";
import ProductStorageHandler from "../persistence/ProductStorageHandle";
import { isDevelopmentEnvironment } from "../estada-config";
import { ChannelType, DeviceType } from "../models/ChannelType";
import { isUiModeProductAdmin } from "../uiMode";
import { SPECIAL_USAGE_TYPES } from "../utils/CommonUtils";

const specialUsageTranslationKeys: Record<string, string> = {
  General: "TabGeneralTemplate.general", // no itx
  "Visualization/Input": "TabGeneralTemplate.visualinput", // no itx
  IPInterface: "TabGeneralTemplate.ipinterface", // no itx
  IPGateway: "TabGeneralTemplate.ipgateway", // no itx
  PowerSupply: "TabGeneralTemplate.powersupply", // no itx
  Unset: "TabGeneralTemplate.unset", // no itx
};

export default function TabGeneralTemplate(props: { device: Device | undefined }) {
  const { device } = props;
  if (!device || !device.template) {
    return null;
  }

  const deviceTemplate = device.template!;

  const redraw = useRedraw();

  const [ruleState, setRuleState] = useState("empty"); // no itx
  const [loadedFrontImage, setFrontImage] = useState<string | undefined>();
  const frontImage = deviceTemplate.useGenericImage || deviceTemplate.isGeneric
    ? device.frontImage : loadedFrontImage;
  const [frontImageSource, setFrontImageSource] = useState("");
  const [noChannelsValue, setNoChannelsValue] = useState(
    deviceTemplate.knxProductData.productInfo.NoChannels ?? "false", // no itx
  );

  function setNoChannelsMetadata(value: string) {
    const nextValue = value === "true" ? "true" : "false"; // no itx
    deviceTemplate.knxProductData.productInfo.NoChannels = nextValue;
    setNoChannelsValue(nextValue);
  }

  async function init() {
    if (deviceTemplate.knxProductData.modelDataId && deviceTemplate.knxProductData.productInfo?.FromEts != "true") {
      await deviceTemplate.knxProductData.loadXmlData(device!);

      const hasRules = !deviceTemplate.knxProductData.currentRules?.empty;
      const hasModifiedRules = !deviceTemplate.knxProductData.modifiedRules.empty;

      let xruleState = "empty"; // no itx
      if (hasRules) {
        xruleState = "yes"; // no itx
      }
      if (hasModifiedRules) {
        xruleState = "modified"; // no itx
      }
      setRuleState(xruleState);
    }
  }

  useEffect(() => {
    updateFrontImage();
    init();
  }, []);

  useOnChangeWatcher(deviceTemplate, () => {
    void updateFrontImage();
    return true;
  });

  useOnChangeWatcher(device, (changedProperty) => {
    if (changedProperty == "template") {
      updateFrontImage();
    }
    return true;
  });
  useOnChangeWatcher(device.channels);

  async function updateFrontImage() {
    if (!deviceTemplate?.useGenericImage && deviceTemplate?.knxProductData.frontImageLoaded == false) {
      await deviceTemplate?.knxProductData.loadFrontImage();
    }

    if (device?.frontImage) {
      setFrontImage(device.frontImage);
      setFrontImageSource(device.template?.useGenericImage || device.template?.isGeneric ? "" : device.template?.knxProductData.frontImageSourceDescription || "");
      return;
    } else {
      setFrontImage(undefined);
      setFrontImageSource("");
    }
  }

  async function reloadProduct() {
    if (deviceTemplate && device) {
      const modelDataId = deviceTemplate.knxProductData.modelDataId;
      if (modelDataId) {
        const newKnxProductData = new KnxProductData({}, deviceTemplate);

        newKnxProductData.modelDataId = modelDataId;
        deviceTemplate.knxProductData = newKnxProductData;

        await ProductStorageHandler.deleteCachedProductData(modelDataId);
      }
    }
  }

  async function onSaveMetadata() {
    if (deviceTemplate && device) {
      const ok = await deviceTemplate.knxProductData.saveMetadataOnProductServer(deviceTemplate, device);
      if (ok) {
        showToast({
          text: state.translate("tabGeneralTemplate.metadataSaved", "Metadata saved"),
        });
      } else {
        showToast({
          isError: true,
          text: state.translate("tabGeneralTemplate.metadataNotSaved", "Metadata coudn`t be saved"),
        });
      }
    } else {
      showToast({
        text: noItx("Error saving Metadata"),
      });
    }
  }

  const actorTypesSelectionElmements: any = [];
  actorTypesSelectionElmements.push(
    <option
      value={ChannelType.Unknown}
      key={"channeltype" + ChannelType.Unknown} // no itx
    >
      {state.translate("tabGeneralDevice.automaticCreation", "automatisch definiert")}
    </option>,
  ); // only from bevore -> only relevant for Actor
  const selectableDeviceType = [
    DeviceType.Switch,
    DeviceType.Dimmer,
    DeviceType.TuneableWhite,
    DeviceType.Heating,
    DeviceType.Jalousie,
    DeviceType.Blind,
    DeviceType.Lock,
    DeviceType.Input,
  ];

  //for (let i in ChannelType) {
  //  const channelType = i;
  selectableDeviceType.forEach((deviceType) => {
    actorTypesSelectionElmements.push(
      <option
        value={deviceType}
        key={"channeltype" + deviceType} // no itx
      >
        {state.translate(`channelType.${deviceType}`, deviceType)}
      </option>,
    );
  });

  return (
    <div
      className="tabGeneral"
      id="tabGeneral"
      style={{
        padding: "5px 20px",
        display: "flex",
        flexFlow: "wrap",
        backgroundColor: "white",
      }}
    >
      <style>
        {" "}
        {`
        table td, table th {
          padding-left: 0px;
        }
      `}
      </style>
      <div
        id="leftColumn"
        style={{
          position: "relative",
          width: "32vw",
          minWidth: "200px",
        }}
      >
        <div
          style={{
            marginTop: "10px",
            backgroundColor: "var(--backgroundColorGrey)",
            color: "var(--white1)",
            padding: "5px",
          }}
        >
          &nbsp;{state.translate("tabGeneralDevice.deviceData", "Gerätedaten")}
        </div>

        <FieldSet>
          <Field label={state.translate("name", "Name")}>
            <input
              key={"name" + deviceTemplate.displayName} // no itx
              className="input"
              maxLength={state.maxCharLimit.text}
              defaultValue={deviceTemplate.displayName}
              onBlur={(ev) => {
                deviceTemplate.displayName = ev.target.value;
              }}
            ></input>
          </Field>

          <Field label={state.translate("type", "Type")}>
            <input
              className="input"
              disabled={true}
              maxLength={state.maxCharLimit.smallText}
              defaultValue={(deviceTemplate?.parent as TemplateGroup)?.templateGroupType?.description?.en}
            />
          </Field>
        </FieldSet>

        {deviceTemplate.knxProductData.productInfo?.FromEts != "true" && (
          <div>
            {" "}
            <div
              style={{
                marginTop: "10px",
                backgroundColor: "var(--backgroundColorGrey)",
                color: "var(--white1)",
                padding: "5px",
              }}
            >
              {itx("tabGeneralTemplate.configStoredOnServer", "Configuration stored on Productserver")}
            </div>
            <FieldSet>
              <Field
                label="Released" // no itx
              >
                <select
                  name="released" // no itx
                  id="released"
                  className="select"
                  defaultValue={deviceTemplate.knxProductData.productInfo.Release}
                  key={`released-${deviceTemplate.knxProductData.productInfo.Release}`}
                  onChange={(ev) => {
                    switch (ev.target.value) {
                      case "true": // no itx
                      case "false": // no itx
                        deviceTemplate.knxProductData.productInfo.Release = ev.target.value;
                        break;
                      default:
                        deviceTemplate.knxProductData.productInfo.Release = undefined;
                    }
                  }}
                >
                  <option value={undefined}>{itx("tabGeneralTemplate.notSet", "Not set")}</option>
                  <option
                    value="true" // no itx
                  >
                    {itx("yes", "Yes")}
                  </option>
                  <option
                    value="false" // no itx
                  >
                    {itx("no", "No")}
                  </option>
                </select>
              </Field>

              <Field
                label="Support Scenes" // no itx
              >
                <select
                  id="supportScenes"
                  className="select"
                  defaultValue={deviceTemplate.knxProductData.productInfo.SupportScenes}
                  key={`supportScenes-${deviceTemplate.knxProductData.productInfo.SupportScenes}`}
                  onChange={(ev) => {
                    switch (ev.target.value) {
                      case "true": // no itx
                      case "false": // no itx
                        deviceTemplate.knxProductData.productInfo.SupportScenes = ev.target.value;
                        break;
                      default:
                        deviceTemplate.knxProductData.productInfo.SupportScenes = undefined;
                    }
                  }}
                >
                  <option value={undefined}>{itx("tabGeneralTemplate.notSet", "Not set")}</option>
                  <option
                    value="true" // no itx
                  >
                    {itx("yes", "Yes")}
                  </option>
                  <option
                    value="false" // no itx
                  >
                    {itx("no", "No")}
                  </option>
                </select>
              </Field>

              {isUiModeProductAdmin() && (
                <Field label={itx("productWizard.productType", "Product type")} key="productType">
                  <input
                    className="input"
                    disabled={true}
                    maxLength={state.maxCharLimit.smallText}
                    value={deviceTemplate.knxProductData.productInfo.ProductType || "-"}
                    readOnly
                  />
                </Field>
              )}

              <Field label={itx("productWizard.channelType", "Channel type")} key="type">
                <select
                  className="select"
                  key={device.channelType}
                  id="TabGeneralDevice_ChannelType"
                  defaultValue={deviceTemplate.knxProductData.productInfo.ChannelType || ChannelType.Unknown}
                  onChange={(ev) => {
                    alert("set device type in config");
                    if (ev.target.value != ChannelType.Unknown) {
                      deviceTemplate.knxProductData.productInfo.ChannelType = ev.target.value;
                    } else {
                      deviceTemplate.knxProductData.productInfo.ChannelType = undefined;
                    }
                  }}
                >
                  {actorTypesSelectionElmements}
                </select>
              </Field>

              <Field
                label="Customizing Scripts" // no itx
              >
                <select
                  name="customizingScripts" // no itx
                  id="customizingScripts"
                  className="select"
                  defaultValue={ruleState}
                  key={`channelDectionMode-${ruleState}`}
                  onChange={(ev) => {
                    switch (ev.target.value) {
                      case "delete": // no itx
                      default:
                    }
                  }}
                >
                  <option
                    value="empty" // no itx
                  >
                    {itx("tabGeneralTemplate.noScript", "No Scripts")}
                  </option>
                  <option
                    value="yes" // no itx
                  >
                    {itx("tabGeneralTemplate.availiable", "Vorhanden")}
                  </option>
                  <option
                    value="modified" // no itx
                  >
                    {itx("tabGeneralTemplate.changedlocal", "Lokal geändert")}
                  </option>
                  <option
                    value="delete" // no itx
                  >
                    {itx("delete", "Delete")}
                  </option>
                </select>
              </Field>
              <Field
                label="No Channels" // no itx
              >
                <select
                  name="noChannels" // no itx
                  id="noChannels"
                  className="select"
                  value={noChannelsValue}
                  onChange={(ev) => setNoChannelsMetadata(ev.target.value)}
                >
                  <option value={noItx("false")}>{itx("false", "false")}</option>
                  <option value={noItx("true")}>{itx("true", "true")}</option>
                </select>
              </Field>
              <Field
                label="Low-level Config" // no itx
              >
                <select
                  name="lowLevelConfig" // no itx
                  id="lowLevelConfig"
                  className="select"
                  value={noChannelsValue}
                  onChange={(ev) => setNoChannelsMetadata(ev.target.value)}
                >
                  <option value={noItx("false")}>{itx("false", "false")}</option>
                  <option value={noItx("true")}>{itx("true", "true")}</option>
                </select>
              </Field>
              <Field
                label="Special Usage" // no itx
              >
                <select
                  name="specialUsage" // no itx
                  id="specialUsage"
                  className="select"
                  defaultValue={deviceTemplate.knxProductData.productInfo.SpecialUsage ?? "Unset"} // no itx
                  key={`specialUsage-${deviceTemplate.knxProductData.productInfo.SpecialUsage}`}
                  onChange={(ev) => {
                    deviceTemplate.knxProductData.productInfo.SpecialUsage = ev.target.value;
                  }}
                >
                  {SPECIAL_USAGE_TYPES.map((specialUsage) => (
                    <option value={specialUsage} key={specialUsage}>
                      {itx(specialUsageTranslationKeys[specialUsage] ?? specialUsage, specialUsage)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label="IsDALI" // no itx
              >
                <select
                  name="isDali" // no itx
                  id="isDali"
                  className="select"
                  defaultValue={
                    deviceTemplate.knxProductData.productInfo.IsDali ?? "false" // no itx
                  }
                  key={`isDali-${deviceTemplate.knxProductData.productInfo.IsDali}`}
                  onChange={(ev) => {
                    switch (ev.target.value) {
                      case "true": // no itx
                        deviceTemplate.knxProductData.productInfo.IsDali = "true"; // no itx
                        break;
                      case "false": // no itx
                        deviceTemplate.knxProductData.productInfo.IsDali = "false"; // no itx
                        break;
                      default:
                        deviceTemplate.knxProductData.productInfo.IsDali = "false"; // no itx
                    }
                  }}
                >
                  <option
                    value={
                      "false" // no itx
                    }
                  >
                    {itx("false", "false")}
                  </option>
                  <option
                    value={
                      "true" // no itx
                    }
                  >
                    {itx("true", "true")}
                  </option>
                </select>
              </Field>
              <Field label="Address Type">
                <select
                  name="deviceAddress" // no itx
                  id="deviceAddress"
                  className="select"
                  defaultValue={deviceTemplate.knxProductData.productInfo.DeviceAddress ?? "Unset"} // no itx
                  key={`deviceAddress-${deviceTemplate.knxProductData.productInfo.DeviceAddress}`}
                  onChange={(ev) => {
                    switch (ev.target.value) {
                      case "0":
                        deviceTemplate.knxProductData.productInfo.DeviceAddress = "0";
                        break;
                      case "1":
                        deviceTemplate.knxProductData.productInfo.DeviceAddress = "1";
                        break;
                      default:
                        deviceTemplate.knxProductData.productInfo.DeviceAddress = "Unset"; // no itx
                    }
                  }}
                >
                  <option value={"0"}>0</option>
                  <option value={"1"}>1</option>
                  <option
                    value={"Unset"} // no itx
                  >
                    {itx("TabGeneralTemplate.unset", "Unset")}
                  </option>
                </select>
              </Field>
            </FieldSet>
            {(device.template?.knxProductData as any)?.currentRules &&
              !(device.template?.knxProductData as any).currentRules.empty && (
                <div style={{ display: "flex", flexDirection: "row", padding: "20px" }}>
                  {state.translate(
                    "tabGeneralDevice.presetParameter",
                    "Dieses Produkt hat von ESTADA voreingestellte Parameter",
                  )}
                </div>
              )}
            {frontImage && (
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  gap: "6px",
                  marginTop: "8px",
                }}
              >
                <img
                  className="device-front-image"
                  style={{ objectFit: "contain" }}
                  src={frontImage}
                  alt=""
                  width={180}
                  height={180}
                  draggable={false}
                />
                {isUiModeProductAdmin() && frontImageSource && (
                  <div style={{ fontSize: "11px", opacity: 0.75, whiteSpace: "pre-line" }}>
                    {itx("tabGeneralDevice.imageSource", "Image source")}: {frontImageSource}
                  </div>
                )}
              </div>
            )}
            {device.isButton && !deviceTemplate.isGeneric && (
              <label style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "6px", marginTop: "8px" }}>
                <input
                  type="checkbox"
                  data-testid="use-generic-image"
                  checked={deviceTemplate.useGenericImage}
                  onChange={(event) => { deviceTemplate.useGenericImage = event.target.checked; }}
                />
                {itx("tabGeneralDevice.useGenericImage", "Generisches Bild verwenden")}
              </label>
            )}
            {isUiModeProductAdmin() &&
              (() => {
                const articleNo = deviceTemplate.orderInformation?.articleNo;
                const variants = deviceTemplate.knxProductData.productInfo?.Variants;
                const hasShopdata =
                  variants ||
                  deviceTemplate.orderInformation?.imageLink ||
                  deviceTemplate.orderInformation?.pageLink ||
                  deviceTemplate.orderInformation?.shopId;
                const noVoltusData = !articleNo || !hasShopdata;
                if (!noVoltusData) return null;
                return (
                  <div
                    style={{ fontSize: "11px", opacity: 0.75, marginTop: "4px", textAlign: "center", color: "#c87000" }}
                  >
                    {itx("tabGeneralDevice.noVoltusData", "No Voltus data attached (no Variants / orderInformation)")}
                  </div>
                );
              })()}
            {/* {isUiModeProductAdmin() && (
              <button
                onClick={() => {
                  // delete preset parameters
                  deletePresetParams();
                }}
                className="button"
              >
                delete preset Parameters
              </button>
            )} */}
            {device.isDummyDeviceForTemplate && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                  marginTop: "16px",
                }}
              >
                <button
                  className="button"
                  style={{ margin: 0 }}
                  onClick={() => {
                    onSaveMetadata();
                  }}
                >
                  {itx("tabGeneralTemplate.saveDataServer", "Save Metadata on Productserver")}
                </button>
                <button
                  className="button"
                  style={{ margin: 0 }}
                  onClick={() => {
                    reloadProduct();
                  }}
                >
                  {itx("tabGeneralTemplate.reloadTemplate", "Reload Product from Server")}
                </button>
              </div>
            )}
            {!device.isDummyDeviceForTemplate && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                {itx(
                  "tabGeneralTemplate.savesettingsProduct",
                  "To save any Customizing or Setting to the Product-Database,",
                )}
                <br />
                {itx("tabGeneralTemplate.openProduct", "open the Product")}
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <i
                    className="las la-external-link-alt"
                    onClick={() => {
                      state.selectionManager.selectedObject = deviceTemplate;
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div id="rightColumn" style={{ display: "flex", flexDirection: "column", width: "50%" }}>
        <DeviceProductDetails device={device} template={deviceTemplate} />
      </div>
    </div>
  );
}
